import { useGetMe } from 'api-hooks/auth/query';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { TourSalesModel } from 'api-hooks/tour/tour-sale';
import { generateId } from 'common/utils/string';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  TourSaleFormType,
  TourSaleItemAddOnsFormType,
  TourSaleItemFormType,
  TourSaleItemGuestFormType,
} from '../components/form-type';

interface Props {
  tourSale?: TourSalesModel;
  defaultValues?: TourSaleFormType;
}

export function addNewAddOns(): TourSaleItemAddOnsFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
  } as never as TourSaleItemAddOnsFormType;
}

export function addNewGuest(): TourSaleItemGuestFormType {
  return {
    bedType: '',
    customId: generateId(),
    groupNumber: '',
    guestId: '',
    guestName: '',
    id: undefined,
    note: '',
    price: undefined,
    tourFlightRefId: '',
    tourPriceRefId: '',
    flightName: '',
    tourFlightId: '',
    tourPriceId: '',
    type: '',
  } as never as TourSaleItemGuestFormType;
}

export function addNewItem(): TourSaleItemFormType {
  return {
    customId: generateId(),
    id: undefined,
    tourSaleAddOns: [],
    tourSaleGuests: [addNewGuest()],
  };
}

export function tourListToTourSale(tourList: TourModel): TourSaleFormType {
  const transactionAt = new Date();
  return {
    address: '',
    adminId: '',
    code: '',
    customerBillingId: '',
    customerBillingOptions: [],
    customerId: '',
    customerStatus: '',
    description: '',
    discount: 0,
    hasDueReceivable: false,
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    needApproval: false,
    number: '',
    operatorId: '',
    otherCost: 0,
    phone: '',
    refId: undefined,
    parentRefId: '',
    salesmanId: '',
    scheduleOptions: [],
    tempTransactionAt: transactionAt,
    transactionAt,
    total: 0,
    tourId: tourList.id,
    tourRefId: tourList.refId,
    typeOptions: [],
    tourSale: undefined,
    tourSales: [addNewItem()],
    tourType: tourList.type,
    enumOptions: [],
  };
}

export function tourSaleToTourSale(tourSale: TourSalesModel): TourSaleFormType {
  const transactionAt = new Date();
  const tour = tourSale?.tourSales?.[0]?.tour;
  const customer = tourSale.customer;
  const billing = tourSale.customerBillingAddress;
  return {
    address: customer.address,
    adminId: tourSale.admin.id,
    code: tourSale.code || '',
    customerBillingId: billing?.id || '',
    customerId: customer.id || '',
    customerStatus: customer.status || '',
    description: tourSale.description || '',
    discount: tourSale.discount || 0,
    hasDueReceivable: customer.hasDueReceivables || false,
    remainingTransactionQuota: null,
    isExceedingQuota: false,
    needApproval: false,
    number: tourSale.invoiceNumber || '',
    operatorId: tourSale?.operator?.id || '',
    otherCost: tourSale.otherCost || 0,
    phone: customer.phone || '',
    refId: undefined,
    parentRefId: '',
    salesmanId: tourSale.salesman?.id || '',
    tempTransactionAt: transactionAt,
    transactionAt,
    total: tourSale.total || 0,
    tourId: tour?.id || '',
    tourRefId: tour?.refId || '',
    tourSale: undefined,
    tourSales: [addNewItem()],
    tourType: tourSale.transactionType,
    customerBillingOptions: [],
    scheduleOptions: [],
    typeOptions: [],
    enumOptions: [],
  };
}

export default function useGetDefaultValues(props: Props) {
  const { tourSale } = props;
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = React.useMemo<TourSaleFormType>(() => {
    const transactionAt = tourSale?.transactionAt ?? new Date();
    if (props.defaultValues) {
      return {
        ...props.defaultValues,
        transactionAt,
        tempTransactionAt: transactionAt,
        refId: undefined,
        tourSale: undefined,
        tourSales: props.defaultValues.tourSales.map((ts) => {
          return {
            ...ts,
            id: undefined,
            tourSaleGuests: ts.tourSaleGuests.map((tsg) => {
              return {
                ...tsg,
                id: undefined,
              };
            }),
            tourSaleAddOns: ts.tourSaleAddOns.map((tsao) => {
              return {
                ...tsao,
                id: undefined,
              };
            }),
          };
        }),
      };
    }
    const customer = tourSale?.customer;
    const tour = tourSale?.tourSales?.[0]?.tour;
    const tourSales: TourSaleFormType['tourSales'] = tourSale?.tourSales?.map(
      (ts) => {
        const tourSaleAddOns: TourSaleItemAddOnsFormType[] =
          ts.tourSaleAddOns.map((tsao) => {
            const guest = tsao.guest;

            return {
              customId: generateId(),
              description: tsao.description || '',
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: tsao.id,
              price: tsao.price,
            };
          });

        const tourSaleGuests: TourSaleItemGuestFormType[] =
          ts.tourSaleGuests.map((tsg) => {
            const guest = tsg.guest;
            const flight = tsg.tourFlight;
            const tourPrice = tsg.tourPrice;
            return {
              guestPreferences: guestPreferencesTransformer(guest),
              customId: generateId(),
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              bedType: tsg.bedType || '',
              groupNumber: tsg.groupNumber || '',
              id: tsg.id,
              note: tsg.note || '',
              price: tsg.price || 0,

              tourFlightId: flight.id || '',
              tourFlightRefId: flight.refId || '',
              flightName: flight.name || '',

              tourPriceId: tourPrice.id,
              tourPriceRefId: tourPrice.refId,
              type: tourPrice.type || '',
            };
          });
        return {
          customId: generateId(),
          id: ts.id,
          tourSaleAddOns,
          tourSaleGuests,
        };
      },
    ) ?? [addNewItem()];
    return {
      address: customer?.address || '',
      adminId: tourSale?.admin.id ?? employeeHook?.id ?? '',
      code: tourSale?.code || '',
      customerBillingId: tourSale?.customerBillingAddress?.id || '',
      customerBillingOptions: [],
      customerId: customer?.id || '',
      customerStatus: customer?.status || '',
      description: tourSale?.description || '',
      discount: tourSale?.discount || 0,
      hasDueReceivable: customer?.hasDueReceivables || false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      needApproval: false,
      number: tourSale?.invoiceNumber || '',
      operatorId: tourSale?.operator?.id || '',
      otherCost: tourSale?.otherCost || 0,
      phone: customer?.phone || '',
      refId: tourSale?.refId,
      parentRefId: tourSale?.parentSale?.refId,
      salesmanId: tourSale?.salesman?.id || '',
      tempTransactionAt: transactionAt,
      transactionAt,
      total: tourSale?.total || 0,
      tourId: tour?.id || '',
      tourRefId: tour?.refId || '',
      tourSale,
      tourSales,
      tourType: tourSale?.transactionType || '',
      scheduleOptions: [],
      enumOptions: [],
      typeOptions: [],
    };
  }, [employeeHook?.id, props.defaultValues, tourSale]);
  return defaultValues;
}
